export const SCORE_DOMAINS = [
  { key: "predictionsScores", label: "Tips", short: "Tips", color: "#00BCD4" },
  { key: "p2pScores", label: "P2P", short: "P2P", color: "#FF7043" },
];

export const WEEKLY_REWARDS = {
  1: { COIN: 5000, CASH: 5000 },
  2: { COIN: 4000, CASH: 4000 },
  3: { COIN: 3500, CASH: 3500 },
  4: { COIN: 3000, CASH: 3000 },
  5: { COIN: 2500, CASH: 2500 },
  6: { COIN: 2000, CASH: 2000 },
  7: { COIN: 1600, CASH: 1600 },
  8: { COIN: 1300, CASH: 1300 },
  9: { COIN: 1100, CASH: 1100 },
  10: { COIN: 1000, CASH: 1000 },
};

export const MIN_REWARD_WEEKLY_SCORE = 50;

export const PODIUM_META = {
  1: { label: "Champion", tone: "gold", height: 118 },
  2: { label: "Runner-up", tone: "silver", height: 96 },
  3: { label: "Third", tone: "bronze", height: 80 },
};

export const WEEKLY_RESET_TZ = "Africa/Kigali";

export function computePeriodScore(row = {}) {
  if (row.currentPeriodScore != null) return Number(row.currentPeriodScore) || 0;
  return SCORE_DOMAINS.reduce((sum, { key }) => sum + (Number(row[key]) || 0), 0);
}

export function formatScore(value, { compact = false } = {}) {
  const n = Number(value) || 0;
  if (compact && n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return n.toFixed(1);
}

export function formatCompactNumber(value) {
  const n = Number(value) || 0;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(Math.round(n));
}

export function scoreBreakdown(row = {}) {
  const total = computePeriodScore(row) || 1;
  return SCORE_DOMAINS.map((domain) => {
    const value = Number(row[domain.key]) || 0;
    return {
      ...domain,
      value,
      pct: total > 0 ? (value / total) * 100 : 0,
    };
  }).filter((d) => d.value > 0);
}

export function dominantDomain(row = {}) {
  const parts = scoreBreakdown(row);
  if (!parts.length) return null;
  return parts.reduce((best, part) => (part.value > best.value ? part : best), parts[0]);
}

export function looksLikeEmail(value) {
  return /\S+@\S+\.\S+/.test(String(value || "").trim());
}

export function looksLikePhone(value) {
  const compact = String(value || "")
    .trim()
    .replace(/[\s().-]/g, "");
  return /^\+?\d{9,15}$/.test(compact);
}

const ROLE_LABELS = new Set([
  "user",
  "agent",
  "ambassador",
  "admin",
  "partner",
  "super_admin",
  "super admin",
]);

function isPrivateIdentity(value) {
  const s = String(value || "").trim();
  if (!s) return true;
  if (looksLikeEmail(s) || looksLikePhone(s)) return true;
  return ROLE_LABELS.has(s.toLowerCase());
}

export function leaderDisplayName(row = {}) {
  const name = String(row.name || "").trim();
  if (name && !isPrivateIdentity(name)) return name;
  const code = String(row.inviteCode || "").trim();
  if (code && !isPrivateIdentity(code)) return code;
  return "Member";
}

export function memberInitials(name, inviteCode) {
  const source = leaderDisplayName({ name, inviteCode }).trim();
  const bits = source.split(/\s+/).filter(Boolean);
  if (bits.length >= 2) return `${bits[0][0]}${bits[1][0]}`.toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

export function matchesLeaderSearch(row, query) {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [leaderDisplayName(row), row.inviteCode, row.userLevel]
    .filter(Boolean)
    .some((field) => String(field).toLowerCase().includes(q));
}

function getZonedParts(date, timeZone) {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = {};
  formatter.formatToParts(date).forEach(({ type, value }) => {
    if (type !== "literal") parts[type] = value;
  });
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
}

function getZonedWeekday(date, timeZone) {
  const weekday = new Intl.DateTimeFormat("en-US", { timeZone, weekday: "short" }).format(date);
  const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return map[weekday] ?? 0;
}

function getTimezoneOffsetMs(timeZone, date) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = {};
  dtf.formatToParts(date).forEach(({ type, value }) => {
    if (type !== "literal") parts[type] = value;
  });
  const asUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );
  return asUTC - date.getTime();
}

function zonedTimeToUtc(year, month, day, hour, minute, second, timeZone) {
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second);
  const offset = getTimezoneOffsetMs(timeZone, new Date(utcGuess));
  return new Date(utcGuess - offset);
}

function addCalendarDays(year, month, day, days) {
  const dt = new Date(Date.UTC(year, month - 1, day + days));
  return { year: dt.getUTCFullYear(), month: dt.getUTCMonth() + 1, day: dt.getUTCDate() };
}

export function getNextWeeklyReset(now = new Date()) {
  const tz = WEEKLY_RESET_TZ;
  const p = getZonedParts(now, tz);
  const dow = getZonedWeekday(now, tz);
  const daysToAdd = (7 - dow) % 7;
  let target = addCalendarDays(p.year, p.month, p.day, daysToAdd);
  let candidate = zonedTimeToUtc(target.year, target.month, target.day, 0, 5, 0, tz);
  if (candidate.getTime() <= now.getTime()) {
    target = addCalendarDays(target.year, target.month, target.day, 7);
    candidate = zonedTimeToUtc(target.year, target.month, target.day, 0, 5, 0, tz);
  }
  return candidate;
}

export function getWeeklyResetCountdown(now = new Date()) {
  const target = getNextWeeklyReset(now);
  let diff = Math.max(0, target.getTime() - now.getTime());
  const days = Math.floor(diff / 86400000);
  diff -= days * 86400000;
  const hours = Math.floor(diff / 3600000);
  diff -= hours * 3600000;
  const minutes = Math.floor(diff / 60000);
  diff -= minutes * 60000;
  const seconds = Math.floor(diff / 1000);
  const weekMs = 7 * 86400000;
  const elapsed = weekMs - Math.max(0, target.getTime() - now.getTime());
  const progress = Math.min(100, Math.max(0, (elapsed / weekMs) * 100));
  return { days, hours, minutes, seconds, target, progress };
}

export function levelTone(level = "") {
  const key = String(level || "Rookie").toLowerCase();
  if (key.includes("legend") || key.includes("master")) return "legend";
  if (key.includes("pro") || key.includes("expert")) return "pro";
  if (key.includes("veteran") || key.includes("elite")) return "elite";
  return "rookie";
}
